import { useEffect, useState } from "react";
import { CloseOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";

import { AppStrings } from "../../../../../../utils/constants/AppStrings";
import { formatString } from "../../../../../../utils/constants/stringUtils";
import {
  formatGrantedTiersLabel,
  getGrantedTierNumbers,
} from "../monthlyChallengeHelpers";
import { getFillPercent } from "./MonthlyTierProgressBar";

const REASON_MAX_LENGTH = 50;
export const EDIT_MODE_STEPS = "steps";
export const EDIT_MODE_TIER = "tier";

const parseBoundedInt = (value, min, max) => {
  const parsed = Number(value);
  const valid =
    value !== "" &&
    Number.isInteger(parsed) &&
    parsed >= min &&
    parsed <= max;
  return { parsed, valid };
};

const formatAdjustment = (from, to, total) =>
  formatString(AppStrings.GAME_PASS_MONTHLY_ADJUSTMENT_RANGE, {
    from: String(from),
    to: String(to),
    total: String(total),
  });

const StepsPreview = ({
  currentCount,
  targetCount,
  goal,
  milestones,
  testId,
}) => {
  const total = milestones.length;
  const currentFill = getFillPercent({
    count: currentCount,
    goal,
    reachedCount: 0,
    total,
    milestones,
  });
  const targetFill = getFillPercent({
    count: targetCount,
    goal,
    reachedCount: 0,
    total,
    milestones,
  });
  const grantsLabel = formatGrantedTiersLabel(
    getGrantedTierNumbers(
      currentCount,
      targetCount,
      milestones.map((milestone) => milestone.threshold),
    ),
  );

  return (
    <div
      className="user-manager-game-pass-monthly-modal-preview"
      data-testid={`${testId}-preview`}
    >
      {total > 0 && (
        <div className="user-manager-game-pass-monthly-modal-preview-track-wrap">
          <div className="user-manager-game-pass-monthly-modal-preview-track">
            <div
              className="user-manager-game-pass-monthly-modal-preview-fill user-manager-game-pass-monthly-modal-preview-fill--forward"
              style={{ width: `${targetFill}%` }}
              data-testid={`${testId}-preview-forward`}
            />
            <div
              className="user-manager-game-pass-monthly-modal-preview-fill user-manager-game-pass-monthly-modal-preview-fill--current"
              style={{ width: `${currentFill}%` }}
              data-testid={`${testId}-preview-current`}
            />
          </div>
          <div className="user-manager-game-pass-monthly-modal-preview-nodes">
            {milestones.map((milestone, index) => (
              <span
                key={`${milestone.threshold}-${index}`}
                className="user-manager-game-pass-monthly-modal-preview-node"
                data-testid={`${testId}-preview-node-${index}`}
              >
                {formatString(AppStrings.GAME_PASS_MONTHLY_TIER_NODE_LABEL, {
                  tier: String(index + 1),
                  threshold: String(milestone.threshold),
                })}
              </span>
            ))}
          </div>
        </div>
      )}
      {grantsLabel && (
        <p
          className="user-manager-game-pass-monthly-modal-grants"
          data-testid={`${testId}-grants`}
        >
          {grantsLabel}
        </p>
      )}
    </div>
  );
};

const EditProgressModal = ({
  open,
  onClose,
  currentTier,
  maxTier,
  currentCount = 0,
  goal = 0,
  milestones = [],
  onSave,
  testId = "monthly-edit-progress-modal",
}) => {
  const [mode, setMode] = useState(EDIT_MODE_STEPS);
  const [tierValue, setTierValue] = useState(String(currentTier ?? 0));
  const [stepValue, setStepValue] = useState(String(currentCount ?? 0));
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setMode(EDIT_MODE_STEPS);
      setTierValue(String(currentTier ?? 0));
      setStepValue(String(currentCount ?? 0));
      setReason("");
    }
  }, [open, currentTier, currentCount]);

  if (!open) {
    return null;
  }

  const safeCurrentCount = Number.isFinite(Number(currentCount))
    ? Number(currentCount)
    : 0;
  const maxSteps = Math.max(safeCurrentCount, Number(goal) || 0);
  const { parsed: parsedTier, valid: isTierValid } = parseBoundedInt(
    tierValue,
    0,
    maxTier,
  );
  const { parsed: parsedSteps, valid: isStepValid } = parseBoundedInt(
    stepValue,
    safeCurrentCount,
    maxSteps,
  );
  const trimmedReason = reason.trim();
  const hasStepChange = isStepValid && parsedSteps > safeCurrentCount;
  const canSave =
    trimmedReason.length > 0 &&
    (mode === EDIT_MODE_STEPS ? hasStepChange : isTierValid);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSave) {
      return;
    }
    if (mode === EDIT_MODE_STEPS) {
      onSave({ mode: EDIT_MODE_STEPS, count: parsedSteps, reason: trimmedReason });
    } else {
      onSave({ mode: EDIT_MODE_TIER, tier: parsedTier, reason: trimmedReason });
    }
    onClose();
  };

  const nudgeSteps = (delta) => {
    const base = isStepValid ? parsedSteps : safeCurrentCount;
    const next = Math.max(
      safeCurrentCount,
      Math.min(maxSteps, base + delta),
    );
    setStepValue(String(next));
  };

  const adjustmentValue =
    mode === EDIT_MODE_STEPS
      ? formatAdjustment(
          safeCurrentCount,
          isStepValid ? parsedSteps : safeCurrentCount,
          maxSteps,
        )
      : formatAdjustment(
          currentTier ?? 0,
          isTierValid ? parsedTier : currentTier ?? 0,
          maxTier,
        );

  return (
    <div
      className="user-manager-game-pass-monthly-modal-overlay"
      data-testid={`${testId}-overlay`}
    >
      <div
        className="user-manager-game-pass-monthly-modal"
        role="dialog"
        aria-modal="true"
        aria-label={AppStrings.MANUAL_PASS_USER}
        data-testid={testId}
      >
        <div className="user-manager-game-pass-monthly-modal-header">
          <h2 className="user-manager-game-pass-monthly-modal-title">
            {AppStrings.MANUAL_PASS_USER}
          </h2>
          <button
            type="button"
            className="user-manager-game-pass-monthly-modal-close"
            onClick={onClose}
            aria-label={AppStrings.CANCEL}
            data-testid={`${testId}-close`}
          >
            <CloseOutlined />
          </button>
        </div>
        <div className="user-manager-game-pass-monthly-modal-divider" />
        <div
          className="user-manager-game-pass-monthly-modal-tabs"
          role="tablist"
          aria-label={AppStrings.MANUAL_PASS_USER}
          data-testid={`${testId}-tabs`}
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === EDIT_MODE_STEPS}
            className={`user-manager-game-pass-monthly-modal-tab${
              mode === EDIT_MODE_STEPS
                ? " user-manager-game-pass-monthly-modal-tab--active"
                : ""
            }`}
            onClick={() => setMode(EDIT_MODE_STEPS)}
            data-testid={`${testId}-tab-steps`}
          >
            {AppStrings.GAME_PASS_MONTHLY_EDIT_STEPS}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === EDIT_MODE_TIER}
            className={`user-manager-game-pass-monthly-modal-tab${
              mode === EDIT_MODE_TIER
                ? " user-manager-game-pass-monthly-modal-tab--active"
                : ""
            }`}
            onClick={() => setMode(EDIT_MODE_TIER)}
            data-testid={`${testId}-tab-tier`}
          >
            {AppStrings.GAME_PASS_MONTHLY_MOVE_TO_TIER}
          </button>
        </div>
        <form onSubmit={handleSubmit} data-testid={`${testId}-form`}>
          {mode === EDIT_MODE_STEPS ? (
            <div className="user-manager-game-pass-monthly-modal-field">
              <label
                className="user-manager-game-pass-monthly-modal-label"
                htmlFor={`${testId}-steps`}
              >
                {AppStrings.GAME_PASS_MONTHLY_MOVE_USER_PROGRESS_TO}
              </label>
              <div className="user-manager-game-pass-monthly-modal-stepper">
                <button
                  type="button"
                  className="user-manager-game-pass-monthly-modal-stepper-btn"
                  onClick={() => nudgeSteps(-1)}
                  disabled={!isStepValid || parsedSteps <= safeCurrentCount}
                  aria-label={AppStrings.GAME_PASS_MONTHLY_DECREASE_STEPS}
                  data-testid={`${testId}-steps-minus`}
                >
                  <MinusOutlined />
                </button>
                <div className="user-manager-game-pass-monthly-modal-tier-input">
                  <input
                    id={`${testId}-steps`}
                    type="number"
                    min={safeCurrentCount}
                    max={maxSteps}
                    step={1}
                    value={stepValue}
                    onChange={(event) => setStepValue(event.target.value)}
                    data-testid={`${testId}-steps`}
                  />
                  <span className="user-manager-game-pass-monthly-modal-tier-max">
                    {`/${maxSteps}`}
                  </span>
                </div>
                <button
                  type="button"
                  className="user-manager-game-pass-monthly-modal-stepper-btn"
                  onClick={() => nudgeSteps(1)}
                  disabled={!isStepValid || parsedSteps >= maxSteps}
                  aria-label={AppStrings.GAME_PASS_MONTHLY_INCREASE_STEPS}
                  data-testid={`${testId}-steps-plus`}
                >
                  <PlusOutlined />
                </button>
              </div>
            </div>
          ) : (
            <div className="user-manager-game-pass-monthly-modal-field">
              <label
                className="user-manager-game-pass-monthly-modal-label"
                htmlFor={`${testId}-tier`}
              >
                {AppStrings.USER_GAME_PASS_MONTHLY_MOVE_USER_TO_TIER}
              </label>
              <div className="user-manager-game-pass-monthly-modal-tier-input">
                <input
                  id={`${testId}-tier`}
                  type="number"
                  min={0}
                  max={maxTier}
                  step={1}
                  value={tierValue}
                  onChange={(event) => setTierValue(event.target.value)}
                  data-testid={`${testId}-tier`}
                />
                <span className="user-manager-game-pass-monthly-modal-tier-max">
                  {`/${maxTier}`}
                </span>
              </div>
            </div>
          )}
          <div className="user-manager-game-pass-monthly-modal-field">
            <span className="user-manager-game-pass-monthly-modal-label">
              {AppStrings.USER_GAME_PASS_MONTHLY_ADJUSTMENT}
            </span>
            <div
              className="user-manager-game-pass-monthly-modal-adjustment"
              data-testid={`${testId}-adjustment`}
            >
              {adjustmentValue}
            </div>
            {mode === EDIT_MODE_STEPS && (
              <StepsPreview
                currentCount={safeCurrentCount}
                targetCount={isStepValid ? parsedSteps : safeCurrentCount}
                goal={maxSteps}
                milestones={milestones}
                testId={testId}
              />
            )}
          </div>
          <div className="user-manager-game-pass-monthly-modal-field">
            <label
              className="user-manager-game-pass-monthly-modal-label"
              htmlFor={`${testId}-reason`}
            >
              {`${AppStrings.REASON}*`}
            </label>
            <div className="user-manager-game-pass-monthly-modal-reason">
              <textarea
                id={`${testId}-reason`}
                value={reason}
                maxLength={REASON_MAX_LENGTH}
                rows={2}
                placeholder={
                  AppStrings.GAME_PASS_MONTHLY_PROGRESS_REASON_PLACEHOLDER
                }
                onChange={(event) => setReason(event.target.value)}
                data-testid={`${testId}-reason`}
              />
              <span className="user-manager-game-pass-monthly-modal-reason-count">
                {`${reason.length}/${REASON_MAX_LENGTH}`}
              </span>
            </div>
          </div>
          <div className="user-manager-game-pass-monthly-modal-divider" />
          <div className="user-manager-game-pass-monthly-modal-actions">
            <button
              type="button"
              className="user-manager-game-pass-monthly-modal-cancel"
              onClick={onClose}
              data-testid={`${testId}-cancel`}
            >
              {AppStrings.CANCEL}
            </button>
            <button
              type="submit"
              className="user-manager-game-pass-monthly-modal-save"
              disabled={!canSave}
              data-testid={`${testId}-save`}
            >
              {AppStrings.SAVE}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProgressModal;
